import React, { useEffect, useState } from 'react';

import type { AuditLogDTO } from '../../../services/apiClient';

import Button from '../../../components/common/Button';
import Card from '../../../components/common/Card';
import FormField from '../../../components/common/FormField';
import AdminLayout from '../../../components/layout/AdminLayout';
import AuditLogTable from '../../../components/tables/AuditLogTable';
import { useAuditLogApi } from '../../../hooks/useAuditLogApi';

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const AuditLogList: React.FC = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [data, setData] = useState<AuditLogDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [tableSearch, setTableSearch] = useState('');
  const [recordIdSearch, setRecordIdSearch] = useState('');
  const [dateStartSearch, setDateStartSearch] = useState(
    formatDate(firstDayOfMonth),
  );
  const [dateEndSearch, setDateEndSearch] = useState(
    formatDate(lastDayOfMonth),
  );

  const [tableOptions, setTableOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const { getTableNames, search } = useAuditLogApi();

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Audit Logs', href: '/admin/auditlogs' },
  ];

  const fetchTableNames = async (): Promise<void> => {
    try {
      const response = await getTableNames();
      if (response && response.data) {
        setTableOptions([
          { value: '', label: 'All Categories' },
          ...response.data.map((table: any) => ({
            value: table.name ?? table.tableName ?? String(table),
            label: table.name ?? table.tableName ?? String(table),
          })),
        ]);
      }
    } catch (error) {
      console.error('Error fetching table names:', error);
    }
  };

  const fetchAuditLogs = async (
    page: number,
    size: number,
    filters = {},
  ): Promise<void> => {
    try {
      setIsLoading(true);

      // Convert date strings to Date objects with proper time adjustments
      const dateStart = dateStartSearch
        ? new Date(`${dateStartSearch}T00:00:00`)
        : undefined;
      const dateEnd = dateEndSearch
        ? new Date(`${dateEndSearch}T23:59:59`)
        : undefined;

      const response = await search({
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: {
          tableName: tableSearch || '',
          recordId: recordIdSearch || undefined,
          dateStart,
          dateEnd,
          ...filters,
        },
      });

      if (response && response.success && response.data) {
        setData(response.data?.results as AuditLogDTO[] || []);
        setTotalRecords(response.data?.totalCount || 0);
        setTotalPages(Math.ceil((response.data?.totalCount || 0) / size));
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTableNames(); // Fetch table names on component mount
    fetchAuditLogs(currentPage, pageSize); // Fetch audit logs
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAuditLogs(1, pageSize);
  };

  const handleClearSearch = (): void => {
    setTableSearch('');
    setRecordIdSearch('');
    setDateStartSearch(formatDate(firstDayOfMonth));
    setDateEndSearch(formatDate(lastDayOfMonth));
    fetchAuditLogs(1, pageSize, {
      tableName: '',
      recordId: undefined,
      dateStart: undefined,
      dateEnd: undefined,
    });
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/auditlogs"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Settings.AuditLog.View'
    >
      <Card
        title="Audit Logs"
        subtitle={`Total Records: ${totalRecords}`}
      >
        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
            <FormField
              id="tableSearch"
              label="Category"
              type="dropdown"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              options={tableOptions}
            />
            <FormField
              id="recordIdSearch"
              label="Record ID"
              value={recordIdSearch}
              onChange={(e) => setRecordIdSearch(e.target.value)}
              placeholder="Search by record ID"
            />
            <FormField
              id="dateStartSearch"
              label="Date Start"
              type="date"
              value={dateStartSearch}
              onChange={(e) => setDateStartSearch(e.target.value)}
            />
            <FormField
              id="dateEndSearch"
              label="Date End"
              type="date"
              value={dateEndSearch}
              onChange={(e) => setDateEndSearch(e.target.value)}
            />
          </div>
          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        {/* Audit Log Table */}
        <AuditLogTable
          data={data}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default AuditLogList;
